import type { PlantGrant, PlantRef, User, UserCapabilities } from '@/types/api'

/**
 * Who may see which plants, and who may change what — read off the session.
 *
 * The server decides all of it and refuses whatever this gets wrong. What this
 * module is for is the screen refusing first: a control that would only earn a
 * 403 is disabled with its reason beside it, and a person with no plant is told
 * so instead of being shown a dozen empty lists.
 *
 * ## The fallback, and why it is generous
 *
 * Every field it reads is optional, because the web and the API deploy apart.
 * A session from an API that predates plant access carries none of them, and
 * then the answer is today's rule — an administrator may do everything, and
 * everybody sees every plant. That is also what the server does on day one:
 * until the owner chooses a glavni administrator, every administrator keeps
 * full rights. Guessing the stricter answer would lock the customer's own
 * administrators out of screens the server still lets them use.
 */

export type Capability = keyof UserCapabilities

export function can(user: User | null | undefined, capability: Capability): boolean {
  if (!user) return false

  const answer = user.can?.[capability]

  if (typeof answer === 'boolean') return answer

  return user.role === 'ADMIN'
}

/** The glavni administrator, or — in compat mode — any administrator. */
export function isSuperAdmin(user: User | null | undefined): boolean {
  if (!user) return false

  return user.isSuperAdmin ?? user.role === 'ADMIN'
}

/**
 * No glavni administrator has been chosen yet.
 *
 * Only an explicit null says so. Absent means the API does not know about the
 * role, and claiming compat mode on its behalf would print a sentence about a
 * rule that is not in force.
 */
export function inCompatMode(user: User | null | undefined): boolean {
  return user?.company?.superAdminId === null
}

/**
 * Whether this person is the chosen glavni administrator.
 *
 * Not the row's `isSuperAdmin`: in compat mode that is true of every
 * administrator, and a badge on each of them would name a role nobody holds.
 */
export function isChosenSuperAdmin(personId: string, viewer: User | null | undefined): boolean {
  const chosen = viewer?.company?.superAdminId

  return typeof chosen === 'string' && chosen === personId
}

/**
 * How much of the company this account sees, in the four cases the copy
 * distinguishes.
 *
 * `all` covers a session with no `plantAccess` at all, for the reason in the
 * module note — the figures it is shown are the whole company's, as they
 * always were.
 */
export type PlantScope = 'all' | 'several' | 'one' | 'none'

export function plantScope(user: User | null | undefined): PlantScope {
  const access = user?.plantAccess

  if (!access || access.all) return 'all'
  if (access.plants.length === 0) return 'none'

  return access.plants.length === 1 ? 'one' : 'several'
}

/**
 * Signed in, and given no plant at all.
 *
 * Never true of an owner: they belong to no company, so there is no plant they
 * could have been given, and the console is not plant-scoped.
 */
export function hasNoPlants(user: User | null | undefined): boolean {
  return Boolean(user) && !user?.isOwner && plantScope(user) === 'none'
}

/** The plants this account may grant to others. Null means every plant. */
export function ownPlantIds(user: User | null | undefined): string[] | null {
  const access = user?.plantAccess

  if (!access || access.all) return null

  return access.plants.map((plant) => plant.id)
}

/**
 * Screens that still mean something without a plant.
 *
 * One's own profile and the guide are about the person, not a plant. The admin
 * section stays open too: a plant-limited administrator with no plant left
 * still has to be able to see that, and the banner above it says the rest.
 */
const OPEN_WITHOUT_PLANTS = ['/profil', '/uputstvo', '/admin']

export function blockedWithoutPlants(pathname: string): boolean {
  return !OPEN_WITHOUT_PLANTS.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
}

/**
 * Whether a sticker that did not resolve is most likely another plant's.
 *
 * `/qr/{token}` answers 404 both for a token nobody issued and for a machine
 * outside the reader's plants — deliberately the same, so a scan cannot be
 * used to learn that a machine exists. For somebody who sees every plant only
 * the first is possible; for anybody else the second is the likelier one, and
 * telling them "unknown code" sends them looking for a new sticker that would
 * not help.
 */
export function qrMissIsOtherPlant(status: number | undefined, user: User | null | undefined): boolean {
  return status === 404 && plantScope(user) !== 'all'
}

// ---------------------------------------------------------------------------
// Editing one person's access
// ---------------------------------------------------------------------------

/**
 * One plant on a person's access list, as the editor may treat it.
 *
 * - `editable`: an administrator's grant inside the editor's own plants — the
 *   only kind this form adds or removes.
 * - `mes`: KaproMES granted it. Locked for everybody, the glavni administrator
 *   included: the sync rewrites the MES's grants on every report of the
 *   person, so removing one here would last until the next one.
 * - `outside`: granted in a plant the editor does not have. Shown so nobody
 *   concludes the person has less than they do; not touchable, because the
 *   server leaves every plant outside the editor's set exactly as it was.
 */
export type ChipState = 'editable' | 'mes' | 'outside'

export interface AccessChip extends PlantRef {
  state: ChipState
}

function inside(plantId: string, editorPlants: string[] | null): boolean {
  return editorPlants === null || editorPlants.includes(plantId)
}

const byName = (a: PlantRef, b: PlantRef) => a.name.localeCompare(b.name)

/** The administrator's grants the form starts from: the part it edits. */
export function initialSelection(granted: PlantGrant[], editorPlants: string[] | null): string[] {
  return granted
    .filter((grant) => grant.source === 'manual' && inside(grant.id, editorPlants))
    .map((grant) => grant.id)
}

/**
 * The chips to draw: the locked ones from what the person has, then the
 * selection, by name.
 *
 * The selection is looked up in `options` — the editor's own plants — so a
 * plant added a moment ago has a name before anything is saved.
 */
export function accessChips(
  granted: PlantGrant[],
  selected: string[],
  options: PlantRef[],
  editorPlants: string[] | null,
): AccessChip[] {
  const chips: AccessChip[] = []

  for (const grant of granted) {
    if (grant.source === 'mes') {
      chips.push({ id: grant.id, code: grant.code, name: grant.name, state: 'mes' })
    } else if (!inside(grant.id, editorPlants)) {
      chips.push({ id: grant.id, code: grant.code, name: grant.name, state: 'outside' })
    }
  }

  for (const id of selected) {
    // The MES chip already says this plant is there; a second, removable chip
    // beside it would offer to take away something it cannot.
    if (chips.some((chip) => chip.id === id)) continue

    const plant = options.find((option) => option.id === id) ?? granted.find((grant) => grant.id === id)

    if (plant) chips.push({ id, code: plant.code, name: plant.name, state: 'editable' })
  }

  return chips.sort(byName)
}

/** The editor's plants not on the list yet — what "Dodaj pogon" offers. */
export function addablePlants(
  options: PlantRef[],
  granted: PlantGrant[],
  selected: string[],
  editorPlants: string[] | null,
): PlantRef[] {
  return options
    .filter(
      (plant) =>
        inside(plant.id, editorPlants) &&
        !selected.includes(plant.id) &&
        !granted.some((grant) => grant.id === plant.id && grant.source === 'mes'),
    )
    .sort(byName)
}

/** Add one plant. A plant outside the editor's set is refused, not queued. */
export function addPlant(selected: string[], plantId: string, editorPlants: string[] | null): string[] {
  if (!inside(plantId, editorPlants) || selected.includes(plantId)) return selected

  return [...selected, plantId]
}

export function removePlant(selected: string[], plantId: string): string[] {
  return selected.filter((id) => id !== plantId)
}

/**
 * Whether the person would see nothing once this is saved.
 *
 * Said in the form rather than discovered on the floor: an account with no
 * plant opens onto a notice and no data, and the person holding it cannot fix
 * that themselves.
 */
export function wouldSeeNothing(
  granted: PlantGrant[],
  selected: string[],
  editorPlants: string[] | null,
  allPlants: boolean,
): boolean {
  if (allPlants) return false

  const kept = granted.some((grant) => grant.source === 'mes' || !inside(grant.id, editorPlants))

  return !kept && selected.length === 0
}

/** Order-insensitive, for "has anything changed". */
export function sameSelection(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id))
}

// ---------------------------------------------------------------------------
// Choosing the glavni administrator
// ---------------------------------------------------------------------------

/**
 * The administrators who would see nothing once a glavni administrator is
 * chosen.
 *
 * Compat mode is what lets every administrator see every plant today. Once it
 * ends, each sees only their own plants — and one with none sees nothing. The
 * candidate is left out: the role itself sees every plant.
 */
export function adminsLeftWithoutPlants<T extends { id: string; plantAccess: { all: boolean; plants: unknown[] } }>(
  admins: T[],
  candidateId: string | null,
): T[] {
  return admins.filter(
    (admin) => admin.id !== candidateId && !admin.plantAccess.all && admin.plantAccess.plants.length === 0,
  )
}
